import { resolvePageSocialImage } from "../../shared/pageSocialImages";
import { pageIsNoIndex, pageAlternateLanguages } from "../../shared/sitePagePolicy";
import React from "react";
import { Helmet } from "react-helmet";
import i18n from "../../translation/i18n";
import { languageList, toHrefLangCode, toOgLocale } from "../SelectLanguage/languageList";
import { getRedirectUrl, getReverseMapping } from "../SelectLanguage/languageRedirects";
import { buildLanguageUrl } from "../../shared/localeRouting";
import { getClientPathname } from "../../shared/clientLocale";

export default function SEO({ title, description, image, canonicalUrl, structuredData, noindex = false }) {
    const currentLanguage = i18n.language || "en";
    const socialImage = resolvePageSocialImage(getClientPathname(), image);
    // URL canonical/hreflang luôn theo sơ đồ production (src/shared/localeRouting.js), kể cả khi chạy local
    const pathnameNoPrefix = getClientPathname();
    let currentFullUrl = canonicalUrl || buildLanguageUrl(currentLanguage, pathnameNoPrefix);

    // Generate hreflang links
    const hreflangLinks = [];
    if (typeof window !== 'undefined') {
        const basePath = getReverseMapping(pathnameNoPrefix);
        const search = ""; // Strip query params for hreflang

        const allowed = pageAlternateLanguages(pathnameNoPrefix);
        languageList.filter(lang => !allowed || allowed.includes(lang.key)).forEach(lang => {
            const langUrl = getRedirectUrl(lang.key, basePath, search);
            if (langUrl) {
                hreflangLinks.push({
                    rel: "alternate",
                    hreflang: toHrefLangCode(lang.key),
                    href: langUrl
                });
            }
        });

        // Add x-default
        const defaultLang = 'en';
        const xDefaultUrl = getRedirectUrl(defaultLang, basePath, search);
        if (xDefaultUrl && (!allowed || allowed.includes('en'))) {
            hreflangLinks.push({
                rel: "alternate",
                hreflang: "x-default",
                href: xDefaultUrl
            });
        }
    }

    // Kiểm tra xem đang ở môi trường client
    if (typeof window !== 'undefined') {
        // Kiểm tra xem đường dẫn hiện tại có phải là một URL ngôn ngữ cơ bản không
        // Worker/Next server sở hữu <head>; Helmet chỉ chạy khi server không đánh dấu vô hiệu hóa
        if (window.__REACT_HELMET_DISABLE__ === true) {
            return null;
        }
    }

    return (
        <Helmet defer={false}>
            <html lang={currentLanguage} />
            <title>{title}</title>
            <meta name='description' content={description} />
            <meta name="language" content={currentLanguage} />
            <meta name="robots" content={noindex || pageIsNoIndex(getClientPathname(), currentLanguage) ? 'noindex,follow' : 'index,follow'} />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content="website" />
            <meta property="og:url" content={currentFullUrl} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={socialImage} />
            <meta property="og:site_name" content="QR Code Generator" />
            <meta property="og:locale" content={toOgLocale(currentLanguage)} />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={currentFullUrl} />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={socialImage} />

            {/* Canonical link */}
            {currentFullUrl && <link rel="canonical" href={currentFullUrl} />}

            {/* Hreflang links */}
            {hreflangLinks.map((link, index) => (
                <link key={index} rel={link.rel} hreflang={link.hreflang} href={link.href} />
            ))}

            {structuredData && (
                <script type="application/ld+json">
                    {JSON.stringify(structuredData)}
                </script>
            )}
        </Helmet>
    )
}
